<template>
  <!-- Loading Spinner -->
  <LoadingSpinner v-if="showPageLoading" />
  
      <div class="min-h-screen bg-gradient-to-br from-[#10103c] via-[#1a1a5c] to-[#2a2a7c] px-4 py-8 pt-40">
    <div class="max-w-4xl mx-auto bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20">
      <div class="p-8">
        <!-- Header -->
        <div class="text-center mb-8">
          <h2 class="text-3xl font-bold text-white mb-2">Creator Onboarding</h2>
          <p class="text-gray-300">Complete your profile and tell us about your YouTube channel</p>
        </div>

        <!-- Form -->
        <form @submit.prevent="handleSubmit" class="space-y-12">
          <!-- Profile Section -->
          <div class="bg-white/5 rounded-lg p-6 mb-8">
            <h3 class="text-xl font-semibold text-white mb-4">Profile Information</h3>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <!-- First Name -->
              <div>
                <label for="firstName" class="block text-base font-medium text-gray-300 mb-2">
                  First Name *
                </label>
                <input
                  id="firstName"
                  v-model="form.firstName"
                  type="text"
                  required
                  class="w-full px-4 py-3 bg-white/10 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter your first name"
                />
              </div>

              <!-- Last Name -->
              <div>
                <label for="lastName" class="block text-base font-medium text-gray-300 mb-2">
                  Last Name *
                </label>
                <input
                  id="lastName"
                  v-model="form.lastName"
                  type="text"
                  required
                  class="w-full px-4 py-3 bg-white/10 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter your last name"
                />
              </div>
            </div>

            <!-- Email -->
            <div class="mb-4">
              <label for="email" class="block text-base font-medium text-gray-300 mb-2">
                Email Address *
              </label>
              <input
                id="email"
                v-model="form.email"
                type="email"
                required
                class="w-full px-4 py-3 bg-white/10 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter your email address"
              />
            </div>

            <!-- YouTube Channel -->
            <div class="mb-4">
              <label for="youtubeChannel" class="block text-base font-medium text-gray-300 mb-2">
                YouTube Channel *
              </label>
              <input
                id="youtubeChannel"
                v-model="form.youtubeChannel"
                type="url"
                required
                class="w-full px-4 py-3 bg-white/10 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                placeholder="https://youtube.com/@yourchannel"
              />
            </div>

            <!-- Subscriber Count -->
            <div>
              <label for="subscriberCount" class="block text-base font-medium text-gray-300 mb-2">
                Subscriber Count *
              </label>
              <input
                id="subscriberCount"
                v-model.number="form.subscriberCount"
                type="number"
                min="0"
                required
                class="w-full px-4 py-3 bg-white/10 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter your current subscriber count"
              />
            </div>
          </div>

          <!-- Channel Information Section -->
          <div class="bg-white/5 rounded-lg p-6">
            <h3 class="text-xl font-semibold text-white mb-6">Channel Information</h3>
            <div class="space-y-8">
            
            <!-- Original Creator -->
          <div>
            <label class="block text-base font-medium text-gray-300 mb-2">
              Are you the original creator of all of the material in your videos (visuals, and audio)? *
            </label>
            <select
              v-model="form.originalCreator"
              required
              class="w-full px-4 py-3 bg-white/10 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">Select an option</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          <!-- Follow-up for Original Creator = No -->
          <div v-if="form.originalCreator === 'no'" :class="{'border-l-4 border-yellow-500 pl-4': form.originalCreator === 'no' && !form.originalCreatorContext?.trim()}">
            <label class="block text-base font-medium mb-2" :class="form.originalCreator === 'no' && !form.originalCreatorContext?.trim() ? 'text-yellow-300' : 'text-gray-300'">
              Please describe what content is not originally created by you:
              <span v-if="form.originalCreator === 'no' && !form.originalCreatorContext?.trim()" class="text-yellow-400 text-xs ml-1">(Context needed)</span>
            </label>
            <textarea
              v-model="form.originalCreatorContext"
              rows="3"
              :class="[
                'w-full px-4 py-3 bg-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200',
                form.originalCreator === 'no' && !form.originalCreatorContext?.trim() 
                  ? 'border-2 border-yellow-500 focus:ring-yellow-500' 
                  : 'border border-gray-600 focus:ring-blue-500'
              ]"
              placeholder="Describe the non-original content..."
            ></textarea>
          </div>

          <!-- Third-party Content -->
          <div>
            <label class="block text-base font-medium text-gray-300 mb-2">
              Do your videos include any third-party content in a major way? (clips, images, music or graphics that you did not create) *
            </label>
            <select
              v-model="form.thirdPartyContent"
              required
              class="w-full px-4 py-3 bg-white/10 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">Select an option</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          <!-- Follow-up for Third-party Content = Yes -->
          <div v-if="form.thirdPartyContent === 'yes'" :class="{'border-l-4 border-yellow-500 pl-4': form.thirdPartyContent === 'yes' && !form.thirdPartyContentContext?.trim()}">
            <label class="block text-base font-medium mb-2" :class="form.thirdPartyContent === 'yes' && !form.thirdPartyContentContext?.trim() ? 'text-yellow-300' : 'text-gray-300'">
              Please describe the third-party content you use and provide licensing details:
              <span v-if="form.thirdPartyContent === 'yes' && !form.thirdPartyContentContext?.trim()" class="text-yellow-400 text-xs ml-1">(Context needed)</span>
            </label>
            <textarea
              v-model="form.thirdPartyContentContext"
              rows="3"
              :class="[
                'w-full px-4 py-3 bg-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200',
                form.thirdPartyContent === 'yes' && !form.thirdPartyContentContext?.trim() 
                  ? 'border-2 border-yellow-500 focus:ring-yellow-500' 
                  : 'border border-gray-600 focus:ring-blue-500'
              ]"
              placeholder="e.g., stock music (licensed), Creative Commons images, fair use clips..."
            ></textarea>
          </div>

          <!-- People Speaking -->
          <div>
            <label class="block text-base font-medium text-gray-300 mb-2">
              Do your videos feature people speaking? (narration, dialogue) *
            </label>
            <select
              v-model="form.peopleSpeaking"
              required
              class="w-full px-4 py-3 bg-white/10 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">Select an option</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          <!-- Follow-up for People Speaking = No -->
          <div v-if="form.peopleSpeaking === 'no'" :class="{'border-l-4 border-yellow-500 pl-4': form.peopleSpeaking === 'no' && !form.peopleSpeakingContext?.trim()}">
            <label class="block text-base font-medium mb-2" :class="form.peopleSpeaking === 'no' && !form.peopleSpeakingContext?.trim() ? 'text-yellow-300' : 'text-gray-300'">
              Please describe the type of content in your videos:
              <span v-if="form.peopleSpeaking === 'no' && !form.peopleSpeakingContext?.trim()" class="text-yellow-400 text-xs ml-1">(Context needed)</span>
            </label>
            <textarea
              v-model="form.peopleSpeakingContext"
              rows="3"
              :class="[
                'w-full px-4 py-3 bg-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200',
                form.peopleSpeaking === 'no' && !form.peopleSpeakingContext?.trim() 
                  ? 'border-2 border-yellow-500 focus:ring-yellow-500' 
                  : 'border border-gray-600 focus:ring-blue-500'
              ]"
              placeholder="e.g., music videos, visual art, time-lapse, etc..."
            ></textarea>
          </div>

          <!-- Primary Language (only show if people speaking = yes) -->
          <div v-if="form.peopleSpeaking === 'yes'">
            <label class="block text-base font-medium text-gray-300 mb-2">
              What's the primary spoken language across your videos? *
            </label>
            <select
              v-model="form.primaryLanguage"
              :required="form.peopleSpeaking === 'yes'"
              class="w-full px-4 py-3 bg-white/10 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">Select a language</option>
              <option v-for="language in languages" :key="language" :value="language">{{ language }}</option>
            </select>
          </div>

          <!-- Multiple Languages (only show if people speaking = yes) -->
          <div v-if="form.peopleSpeaking === 'yes'">
            <label class="block text-base font-medium text-gray-300 mb-2">
              Do your videos feature more than one spoken language? *
            </label>
            <select
              v-model="form.multipleLanguages"
              :required="form.peopleSpeaking === 'yes'"
              class="w-full px-4 py-3 bg-white/10 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">Select an option</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          <!-- Additional Languages (only show if multipleLanguages = yes) -->
          <div v-if="form.multipleLanguages === 'yes'">
            <label class="block text-base font-medium text-gray-300 mb-2">
              Select additional languages featured in your videos: *
            </label>
            <div class="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 bg-white/5 rounded-lg">
              <label v-for="language in languages" :key="language" class="flex items-center text-gray-300 hover:text-white cursor-pointer">
                <input
                  type="checkbox"
                  :value="language"
                  v-model="form.additionalLanguages"
                  class="mr-2 text-blue-500 focus:ring-blue-500"
                />
                {{ language }}
              </label>
            </div>
          </div>

          <!-- Production Country -->
          <div>
            <label class="block text-base font-medium text-gray-300 mb-2">
              In which country/region did you produce the majority of your channel's content? (If you shoot your videos all over the world, add your homebase region) *
            </label>
            <select
              v-model="form.productionCountry"
              required
              class="w-full px-4 py-3 bg-white/10 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">Select a country/region</option>
              <option v-for="country in countries" :key="country" :value="country">{{ country }}</option>
            </select>
          </div>

          <!-- Production City -->
          <div v-if="form.productionCountry">
            <label class="block text-base font-medium text-gray-300 mb-2">
              In which city did you produce the majority of your content? (Or your homebase city) *
            </label>
            <input
              type="text"
              v-model="form.productionCity"
              required
              placeholder="Enter the city name"
              class="w-full px-4 py-3 bg-white/10 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            />
          </div>

          <!-- Age Restrictions -->
          <div>
            <label class="block text-base font-medium text-gray-300 mb-2">
              Do any of your videos carry age restrictions, or sensitive content warnings? *
            </label>
            <select
              v-model="form.ageRestrictions"
              required
              class="w-full px-4 py-3 bg-white/10 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">Select an option</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          <!-- Follow-up for Age Restrictions = Yes -->
          <div v-if="form.ageRestrictions === 'yes'" :class="{'border-l-4 border-yellow-500 pl-4': form.ageRestrictions === 'yes' && !form.ageRestrictionsContext?.trim()}">
            <label class="block text-base font-medium mb-2" :class="form.ageRestrictions === 'yes' && !form.ageRestrictionsContext?.trim() ? 'text-yellow-300' : 'text-gray-300'">
              Please describe the age restrictions or sensitive content warnings on your videos:
              <span v-if="form.ageRestrictions === 'yes' && !form.ageRestrictionsContext?.trim()" class="text-yellow-400 text-xs ml-1">(Context needed)</span>
            </label>
            <textarea
              v-model="form.ageRestrictionsContext"
              rows="3"
              :class="[
                'w-full px-4 py-3 bg-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200',
                form.ageRestrictions === 'yes' && !form.ageRestrictionsContext?.trim() 
                  ? 'border-2 border-yellow-500 focus:ring-yellow-500' 
                  : 'border border-gray-600 focus:ring-blue-500'
              ]"
              placeholder="e.g., mature themes, violence, strong language, adult content..."
            ></textarea>
          </div>

          <!-- AI Generated -->
          <div>
            <label class="block text-base font-medium text-gray-300 mb-2">
              Are any of your videos generated or enhanced by AI tools? *
            </label>
            <select
              v-model="form.aiGenerated"
              required
              class="w-full px-4 py-3 bg-white/10 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">Select an option</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          <!-- Follow-up for AI Generated = Yes -->
          <div v-if="form.aiGenerated === 'yes'" :class="{'border-l-4 border-yellow-500 pl-4': form.aiGenerated === 'yes' && !form.aiGeneratedContext?.trim()}">
            <label class="block text-base font-medium mb-2" :class="form.aiGenerated === 'yes' && !form.aiGeneratedContext?.trim() ? 'text-yellow-300' : 'text-gray-300'">
              Please describe which AI tools you use and how they enhance your content:
              <span v-if="form.aiGenerated === 'yes' && !form.aiGeneratedContext?.trim()" class="text-yellow-400 text-xs ml-1">(Context needed)</span>
            </label>
            <textarea
              v-model="form.aiGeneratedContext"
              rows="3"
              :class="[
                'w-full px-4 py-3 bg-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200',
                form.aiGenerated === 'yes' && !form.aiGeneratedContext?.trim() 
                  ? 'border-2 border-yellow-500 focus:ring-yellow-500' 
                  : 'border border-gray-600 focus:ring-blue-500'
              ]"
              placeholder="e.g., ChatGPT for scripts, Midjourney for thumbnails, voice synthesis..."
            ></textarea>
          </div>

          <!-- Channel Categories -->
          <div>
            <label class="block text-base font-medium text-gray-300 mb-2">
              Which categories describe your channel the best? (Select all that apply) *
            </label>
            <div class="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto p-4 bg-white/5 rounded-lg mb-4">
              <label v-for="category in categories" :key="category" class="flex items-center text-gray-300 hover:text-white cursor-pointer">
                <input
                  type="checkbox"
                  :value="category"
                  v-model="form.channelCategories"
                  class="mr-2 text-blue-500 focus:ring-blue-500"
                />
                {{ category }}
              </label>
              <label class="flex items-center text-gray-300 hover:text-white cursor-pointer">
                <input
                  type="checkbox"
                  value="Other"
                  v-model="form.channelCategories"
                  class="mr-2 text-blue-500 focus:ring-blue-500"
                />
                Other
              </label>
            </div>
            
            <!-- Other Category Input -->
            <div v-if="form.channelCategories.includes('Other')">
              <label class="block text-base font-medium text-gray-300 mb-2">
                Please specify other category:
              </label>
              <input
                type="text"
                v-model="form.otherCategory"
                placeholder="Enter your custom category"
                class="w-full px-4 py-3 bg-white/10 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
            </div>
          </div>
          </div>
          </div>

          <!-- Terms of Use Acceptance -->
          <div class="bg-blue-500/20 border border-blue-500/30 rounded-lg p-6">
            <div class="flex items-start space-x-3">
              <input
                id="termsAccepted"
                v-model="form.termsAccepted"
                type="checkbox"
                :true-value="true"
                :false-value="false"
                required
                class="mt-1 h-4 w-4 text-blue-600 bg-white/10 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
              />
              <label for="termsAccepted" class="text-base text-gray-300 leading-relaxed">
                I have read and agree to the 
                <router-link 
                  to="/terms-of-use" 
                  target="_blank"
                  class="text-blue-400 hover:text-blue-300 underline font-medium"
                >
                  Terms of Use
                </router-link> 
                of Rendery Consortium, including the governance process, content licensing rights, prohibited AI use cases, and liability limitations. *
              </label>
            </div>
          </div>

          <!-- Error Message -->
          <div v-if="errorMessage" class="p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
            <p class="text-red-200 text-sm">{{ errorMessage }}</p>
          </div>

          <!-- Success Message -->
          <div v-if="successMessage" class="p-4 bg-green-500/20 border border-green-500/50 rounded-lg">
            <p class="text-green-200 text-sm">{{ successMessage }}</p>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            :disabled="isLoading"
            class="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 disabled:scale-100"
          >
            <span v-if="!isLoading">Complete Registration</span>
            <span v-else class="flex items-center justify-center">
              <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Submitting...
            </span>
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import { apiCall } from '../config/api.js';

export default {
  name: 'AdditionalQuestions',
  components: {
    LoadingSpinner
  },
  data() {
    return {
      form: {
        // Profile fields
        firstName: '',
        lastName: '',
        email: '',
        youtubeChannel: '',
        subscriberCount: 0,
        // Additional questions fields
        originalCreator: '',
        originalCreatorContext: '',
        thirdPartyContent: '',
        thirdPartyContentContext: '',
        peopleSpeaking: '',
        peopleSpeakingContext: '',
        primaryLanguage: '',
        multipleLanguages: '',
        additionalLanguages: [],
        productionCountry: '',
        productionCity: '',
        ageRestrictions: '',
        ageRestrictionsContext: '',
        aiGenerated: '',
        aiGeneratedContext: '',
        channelCategories: [],
        otherCategory: '',
        termsAccepted: false
      },
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      userId: null,
      showPageLoading: false,
      showValidationErrors: false,
      languages: [
        'English', 'Spanish', 'French', 'German', 'Italian', 'Portuguese', 'Russian', 'Japanese', 'Korean', 'Chinese (Mandarin)',
        'Chinese (Cantonese)', 'Arabic', 'Hindi', 'Bengali', 'Punjabi', 'Telugu', 'Marathi', 'Tamil', 'Urdu', 'Gujarati',
        'Malayalam', 'Kannada', 'Odia', 'Dutch', 'Polish', 'Turkish', 'Swedish', 'Norwegian', 'Danish', 'Finnish',
        'Greek', 'Hebrew', 'Thai', 'Vietnamese', 'Indonesian', 'Malay', 'Filipino', 'Swahili', 'Amharic', 'Yoruba',
        'Igbo', 'Hausa', 'Zulu', 'Afrikaans', 'Xhosa', 'Other'
      ],
      countries: [
        'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina', 'Armenia', 'Australia', 'Austria',
        'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan',
        'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia',
        'Cameroon', 'Canada', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo (Congo-Brazzaville)', 'Congo (Democratic Republic)',
        'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador',
        'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France',
        'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau',
        'Guyana', 'Haiti', 'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland',
        'Israel', 'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Kuwait', 'Kyrgyzstan',
        'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Madagascar',
        'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia',
        'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal',
        'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway', 'Oman', 'Pakistan',
        'Palau', 'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar',
        'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia',
        'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa',
        'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria', 'Taiwan',
        'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan',
        'Tuvalu', 'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City',
        'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
      ],
      categories: [
        'Gaming', 'Entertainment', 'Music', 'Sports', 'News & Politics', 'Education', 'Science & Technology',
        'Comedy', 'Film & Animation', 'Autos & Vehicles', 'Pets & Animals', 'Travel & Events', 'Howto & Style',
        'People & Blogs', 'Nonprofits & Activism', 'Beauty & Fashion', 'Food & Cooking', 'Health & Fitness',
        'DIY & Crafts', 'Business & Finance', 'Vlogs', 'Reviews & Unboxing', 'Tutorials', 'Documentary',
        'Kids & Family', 'Art & Design', 'Photography', 'Real Estate', 'Lifestyle', 'Motivation & Self-Help'
      ]
    };
  },
  computed: {
    isFormValid() {
      const missingFields = this.getMissingFields();
      return missingFields.length === 0;
    }
  },
  watch: {
    'form.productionCountry'(newValue) {
      // Clear city when country changes
      if (!newValue) {
        this.form.productionCity = '';
      }
    },
    'form.channelCategories'(newValue) {
      // Clear other category when "Other" is deselected
      if (!newValue.includes('Other')) {
        this.form.otherCategory = '';
      }
    }
  },
  mounted() {
    // Get user ID from route params or local storage
    this.userId = this.$route.params.userId || localStorage.getItem('tempUserId');
    
    if (!this.userId) {
      this.$router.push('/');
      return;
    }

    // Pre-fill email if available from registration
    const tempEmail = localStorage.getItem('tempUserEmail');
    if (tempEmail) {
      this.form.email = tempEmail;
    }
  },
  methods: {
    getMissingFields() {
      const missingFields = [];
      
      // Profile fields validation
      if (!this.form.firstName?.trim()) missingFields.push('First Name');
      if (!this.form.lastName?.trim()) missingFields.push('Last Name');
      if (!this.form.email?.trim()) missingFields.push('Email Address');
      if (!this.form.youtubeChannel?.trim()) missingFields.push('YouTube Channel');
      if (typeof this.form.subscriberCount !== 'number' || this.form.subscriberCount < 0) {
        missingFields.push('Subscriber Count');
      }
      
      // Required dropdown fields
      if (!this.form.originalCreator) missingFields.push('Original Creator question');
      if (!this.form.thirdPartyContent) missingFields.push('Third-party Content question');
      if (!this.form.peopleSpeaking) missingFields.push('People Speaking question');
      if (!this.form.productionCountry) missingFields.push('Production Country');
      if (!this.form.ageRestrictions) missingFields.push('Age Restrictions question');
      if (!this.form.aiGenerated) missingFields.push('AI Generated question');
      
      // Conditional context fields
      if (this.form.originalCreator === 'no' && !this.form.originalCreatorContext?.trim()) {
        missingFields.push('Original Creator context (explanation required)');
      }
      if (this.form.thirdPartyContent === 'yes' && !this.form.thirdPartyContentContext?.trim()) {
        missingFields.push('Third-party Content context (explanation required)');
      }
      if (this.form.peopleSpeaking === 'no' && !this.form.peopleSpeakingContext?.trim()) {
        missingFields.push('People Speaking context (explanation required)');
      }
      if (this.form.ageRestrictions === 'yes' && !this.form.ageRestrictionsContext?.trim()) {
        missingFields.push('Age Restrictions context (explanation required)');
      }
      if (this.form.aiGenerated === 'yes' && !this.form.aiGeneratedContext?.trim()) {
        missingFields.push('AI Generated context (explanation required)');
      }
      
      // Production city (required when country is selected)
      if (this.form.productionCountry && !this.form.productionCity?.trim()) {
        missingFields.push('Production City');
      }
      
      // Language requirements (when people speaking = yes)
      if (this.form.peopleSpeaking === 'yes') {
        if (!this.form.primaryLanguage) missingFields.push('Primary Language');
        if (!this.form.multipleLanguages) missingFields.push('Multiple Languages question');
        if (this.form.multipleLanguages === 'yes' && this.form.additionalLanguages.length === 0) {
          missingFields.push('Additional Languages (at least one must be selected)');
        }
      }
      
      // Channel categories
      if (this.form.channelCategories.length === 0) {
        missingFields.push('Channel Categories (at least one must be selected)');
      }
      
      // Other category (when "Other" is selected)
      if (this.form.channelCategories.includes('Other') && !this.form.otherCategory?.trim()) {
        missingFields.push('Other Category specification');
      }
      
      // Terms of Use acceptance
      if (!this.form.termsAccepted) {
        missingFields.push('Terms of Use acceptance');
      }
      
      return missingFields;
    },
    async handleSubmit() {
      const missingFields = this.getMissingFields();
      
      if (missingFields.length > 0) {
        this.showValidationErrors = true;
        this.errorMessage = `Please complete the following required fields:\n\n• ${missingFields.join('\n• ')}`;
        return;
      }

      this.isLoading = true;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        // Store user email temporarily for the completion page
        if (this.$auth && this.$auth.user && this.$auth.user.email) {
          localStorage.setItem('tempUserEmail', this.$auth.user.email);
        }

        // Prepare form data - clean up language fields when not needed
        const formData = { ...this.form };
        
        // If people speaking is 'no', clear language-related fields
        if (formData.peopleSpeaking === 'no') {
          formData.primaryLanguage = '';
          formData.multipleLanguages = '';
          formData.additionalLanguages = [];
        }

        // Debug: Log the form data being sent
        if (process.env.NODE_ENV === 'development') {
          console.log('Form data being sent:', JSON.stringify(formData, null, 2));
        }

        // Send data to backend API
        const { response, data } = await apiCall(`users/${this.userId}/additional-questions`, {
          method: 'PUT',
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          if (process.env.NODE_ENV === 'development') {
            console.error('Backend validation errors:', data);
          }
          throw new Error(`HTTP error! status: ${response.status} - ${JSON.stringify(data.message || data)}`);
        }
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Creator onboarding saved:', data);
        }
        
        // Clear stored data
        localStorage.removeItem('tempUserId');
        localStorage.removeItem('tempUserEmail');
        
        // Show page loading animation and redirect
        this.showPageLoading = true;
        setTimeout(() => {
          this.$router.push('/registration-complete');
        }, 1000);
        
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Submission error:', error);
        }
        this.errorMessage = 'Failed to submit questionnaire. Please try again.';
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped>
/* Custom scrollbar styling */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

/* Fix select dropdown options visibility */
select option {
  background-color: #9333ea !important; /* purple-600 */
  color: white !important;
}

/* Ensure select elements have proper styling */
select {
  color-scheme: dark;
}
</style> 